import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Truck, ShieldCheck, Palette, Star } from "lucide-react";

const features = [
  { icon: Palette, title: "Custom Σχέδια", desc: "Οποιοδήποτε κείμενο, σχέδιο ή λογότυπο σε flex neon." },
  { icon: Truck, title: "Παράδοση & Αντικαταβολή", desc: "Σε όλη την Ελλάδα. Πληρωμή και στην παράδοση." },
  { icon: ShieldCheck, title: "Εγγύηση 2 Έτη", desc: "Ποιότητα premium LED flex με χαμηλή κατανάλωση." },
  { icon: Sparkles, title: "Χειροποίητο", desc: "Κάθε κομμάτι φτιάχνεται με προσοχή στη λεπτομέρεια." },
];

export const HomePage = () => {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 neon-grid-bg opacity-40 pointer-events-none" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 80% 50% at 70% 40%, rgba(176,38,255,0.18), transparent 60%), radial-gradient(ellipse 60% 40% at 20% 70%, rgba(0,255,255,0.1), transparent 70%)",
          }}
        />

        {/* Purple lightning bolt decoration */}
        <motion.svg
          aria-hidden="true"
          viewBox="0 0 200 400"
          className="pointer-events-none absolute -left-4 top-0 w-[140px] sm:w-[180px] md:w-[220px] lg:w-[260px] opacity-50 hidden sm:block"
          initial={{ opacity: 0, scale: 0.9, rotate: -12 }}
          animate={{ opacity: 0.55, scale: 1, rotate: -10 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          style={{
            filter:
              "drop-shadow(0 0 14px rgba(176,38,255,0.9)) drop-shadow(0 0 36px rgba(176,38,255,0.7)) drop-shadow(0 0 70px rgba(176,38,255,0.45))",
          }}
        >
          <defs>
            <linearGradient id="bolt-grad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#E0AEFF" />
              <stop offset="45%" stopColor="#B026FF" />
              <stop offset="100%" stopColor="#7A00CC" />
            </linearGradient>
          </defs>
          <motion.path
            d="M120 0 L40 200 L95 200 L60 400 L170 170 L110 170 L165 0 Z"
            fill="url(#bolt-grad)"
            stroke="#F0D4FF"
            strokeWidth="2"
            strokeLinejoin="round"
            animate={{ opacity: [1, 0.7, 1, 0.85, 1] }}
            transition={{ duration: 4, repeat: Infinity, times: [0, 0.45, 0.5, 0.6, 1] }}
          />
        </motion.svg>

        {/* Secondary smaller bolt */}
        <motion.svg
          aria-hidden="true"
          viewBox="0 0 200 400"
          className="pointer-events-none hidden lg:block absolute right-[2%] bottom-[8%] w-[130px] xl:w-[170px] opacity-45"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5, rotate: 14 }}
          transition={{ duration: 1.2, ease: "easeOut", delay: 0.3 }}
          style={{
            filter:
              "drop-shadow(0 0 10px rgba(176,38,255,0.8)) drop-shadow(0 0 30px rgba(176,38,255,0.5))",
          }}
        >
          <motion.path
            d="M120 0 L40 200 L95 200 L60 400 L170 170 L110 170 L165 0 Z"
            fill="url(#bolt-grad)"
            stroke="#F0D4FF"
            strokeWidth="2"
            strokeLinejoin="round"
            animate={{ opacity: [0.6, 0.9, 0.5, 0.8, 0.65] }}
            transition={{ duration: 5, repeat: Infinity, delay: 1 }}
          />
        </motion.svg>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col gap-8"
          >
            <div className="flex items-center gap-2">
              <span className="text-xs tracking-[0.3em] uppercase text-[#B026FF] font-bold">GKLED</span>
              <span className="text-xs text-zinc-500">Flex Neon Signs</span>
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tighter">
              Φώτισε τον<br />
              <span className="text-[#B026FF] neon-glow-pink animate-flicker">χώρο σου</span><br />
              με <span className="tracking-normal pr-1">neon</span>.
            </h1>
            <p className="text-zinc-400 text-lg max-w-lg leading-relaxed">
              Χειροποίητες flex neon επιγραφές για σπίτι, επιχείρηση, γάμο ή δώρο.
              Έτοιμα σχέδια ή custom από εσένα.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/artworks"
                className="inline-flex items-center justify-center gap-2 bg-[#B026FF] text-black px-8 py-4 rounded-lg font-bold hover:neon-box-glow-pink transition-all duration-300"
                data-testid="hero-cta-artworks"
              >
                Δες τα Artworks <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-8 py-4 rounded-lg font-medium hover:border-[#00FFFF] hover:text-[#00FFFF] transition-all duration-300"
                data-testid="hero-cta-custom"
              >
                Custom Σχεδιασμός
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            {/* External purple glow behind the card */}
            <div
              className="pointer-events-none absolute -inset-8 -z-10 blur-3xl opacity-70"
              style={{
                background:
                  "radial-gradient(ellipse 70% 55% at 50% 50%, rgba(176,38,255,0.4), transparent 70%)",
              }}
            />
            <div
              className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-white/5"
              style={{
                backgroundColor: "#000000",
                backgroundImage: `url(${process.env.REACT_APP_BACKEND_URL}/api/images/8d0e2198c4b9485e94b080bddd0b3d0b)`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center px-6">
                <div className="w-20 h-20 rounded-full border-2 border-dashed border-[#B026FF]/40 flex items-center justify-center bg-black/30">
                  <Sparkles size={28} className="text-[#B026FF]/70" />
                </div>
                <div className="text-sm uppercase tracking-[0.25em] text-zinc-200/80 font-bold">
                  Σύντομα
                </div>
                <div className="text-xs text-zinc-400/60 max-w-xs">
                  Νέες δημιουργίες on the way
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 md:py-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="p-6 rounded-2xl border border-white/5 bg-[#050505] hover:border-[#B026FF]/30 transition-colors"
              data-testid={`feature-${i}`}
            >
              <f.icon size={24} className="text-[#B026FF] mb-4" strokeWidth={1.5} />
              <div className="font-heading font-semibold text-lg mb-2">{f.title}</div>
              <div className="text-sm text-zinc-400 leading-relaxed">{f.desc}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="relative overflow-hidden rounded-3xl border border-white/5 bg-[#050505] p-10 md:p-16">
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse 60% 80% at 100% 50%, rgba(0,255,255,0.15), transparent 60%)",
              }}
            />
            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="text-xs tracking-[0.3em] uppercase text-[#00FFFF] font-bold mb-3">Custom Neon</div>
                <h3 className="font-heading text-3xl md:text-4xl font-black mb-4">
                  Η δική σου φράση σε <span className="text-[#00FFFF] neon-glow-cyan">flex neon</span>
                </h3>
                <p className="text-zinc-400 mb-6 max-w-md">
                  Στείλε μας το κείμενο, το χρώμα και τις διαστάσεις που θες.
                  Σου παραδίδουμε το δικό σου custom neon σε 2-5 εργάσιμες.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-[#00FFFF] text-black px-6 py-3 rounded-lg font-bold hover:neon-box-glow-cyan transition-all"
                  data-testid="cta-custom-contact"
                >
                  Ξεκίνα το σχέδιό σου <ArrowRight size={16} />
                </Link>
              </div>
              <div className="hidden md:block">
                <div className="font-heading text-7xl lg:text-8xl font-black text-right text-[#00FFFF] neon-glow-cyan leading-none">
                  GKLED
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
